import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { t } from '@/lib/i18n';
import { InlineEntityList } from '../InlineEntityList';
import type { InlineFieldDef } from '../InlineEntityList';
import {
  useRorosTargetMarkets,
  useRorosTargetMarketCreate,
  useRorosTargetMarketUpdate,
  useRorosTargetMarketRemove,
  useRorosTargetMarketReorder,
} from '@/hooks/roros/overarching';
import type { RorosTargetMarketDTO } from '@/types/roros/overarching';
import { useOverarchingEditable } from '@/routes/roros/overarching/OverarchingEditableContext';
import { IcpSubtree } from './ICPList';

// ─── Top-level Markets ───────────────────────────────────────────────────────

export function MarketTree() {
  const marketsQuery = useRorosTargetMarkets({ pageSize: '100' });
  const createMarket = useRorosTargetMarketCreate();
  const updateMarket = useRorosTargetMarketUpdate();
  const removeMarket = useRorosTargetMarketRemove();
  const reorderMarkets = useRorosTargetMarketReorder();
  const { canEdit } = useOverarchingEditable();

  const markets = (marketsQuery.data ?? []) as RorosTargetMarketDTO[];

  const fields: InlineFieldDef<RorosTargetMarketDTO>[] = [
    { name: 'name', label: t('Name'), type: 'text', required: true, placeholder: t('e.g. Mittelstand Germany') },
    { name: 'description', label: t('Description'), type: 'textarea', placeholder: t('Who is this market?') },
    { name: 'sizeEstimate', label: t('Size estimate'), type: 'text', placeholder: t('e.g. 5,000 companies / €1B TAM') },
    { name: 'geography', label: t('Geography'), type: 'text', placeholder: t('e.g. DACH') },
  ];

  async function handleReorder(index: number, direction: 'up' | 'down') {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    await reorderMarkets.mutateAsync([
      { id: markets[index].id, sortOrder: markets[swapIdx].sortOrder },
      { id: markets[swapIdx].id, sortOrder: markets[index].sortOrder },
    ]);
  }

  return (
    <InlineEntityList<RorosTargetMarketDTO>
      title={t('Target Markets')}
      emptyHint={t('No target markets yet. Add your first one to start mapping your customers.')}
      items={markets}
      fields={fields}
      addLabel={t('Add Market')}
      readOnly={!canEdit}
      onCreate={async (draft) => { await createMarket.mutateAsync(draft as Record<string, unknown>); }}
      onUpdate={async (id, patch) => { await updateMarket.mutateAsync({ id, payload: patch as Record<string, unknown> }); }}
      onDelete={async (id) => { await removeMarket.mutateAsync(id); }}
      onReorder={handleReorder}
      renderRow={(market) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{market.name}</p>
          {market.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{market.description}</p>
          )}
          <div className="flex gap-1 mt-1">
            {market.geography && <Badge variant="secondary" className="text-[10px]">{market.geography}</Badge>}
            {market.sizeEstimate && <Badge variant="secondary" className="text-[10px]">{market.sizeEstimate}</Badge>}
          </div>
        </div>
      )}
      renderExpanded={(market) => <IcpSubtree marketId={market.id} />}
    />
  );
}
